import { Link } from 'react-router';
import { Brand, Button, Icon } from '@efira/ui';

import { AccountMenu } from './topbar/AccountMenu';
import { SoundBar } from './topbar/SoundBar';
import { Notifications } from './topbar/Notifications';
import { OverlayUrl } from './topbar/OverlayUrl';
import { QuotaPill, QuotaPromo } from './topbar/QuotaPill';
import { Widgets } from './topbar/Widgets';
import { useConsoleSession } from './session';

/**
 * Верхняя полоса консоли: 48 пикселей, разрезана по границам колонок.
 *
 * Каждая треть ПРИНАДЛЕЖИТ СВОЕЙ КОЛОНКЕ и едет вместе с её границей
 * (docs/reference/LIVECADE_UX.md, раздел 3). Поэтому полоса не отдельный
 * элемент над сеткой, а верхний ряд той же сетки.
 *
 * В полосе живёт то, что обязано быть видно ВСЕГДА: состояние сессии, остаток
 * эфира, ссылка оверлея. Всё остальное - колонки, а они прокручиваются.
 *
 * КАЖДАЯ ТРЕТЬ ДЕГРАДИРУЕТ САМА. Границы колонок тянутся мышью, поэтому любая
 * треть в любой момент может стать узкой - и ширина окна об этом ничего не
 * говорит. Отсюда два правила:
 *
 *   1. треть - контейнер запроса (`@container`), и всё второстепенное в ней
 *      прячется по ШИРИНЕ ТРЕТИ (`@[...]:`), а не по ширине окна (`sm:`);
 *   2. треть не выпускает содержимое наружу (`overflow-hidden`, `min-w-0`),
 *      иначе элементы наезжают на соседнюю колонку.
 *
 * Дефект, который это чинит: «Pro скоро» наезжал на «ЗВУКИ · GIF · МУЗЫКА»
 * при узком центре - обе трети верили ширине окна, а не своей собственной.
 *
 * ЛЕСТНИЦА ДЕГРАДАЦИИ ЦЕНТРАЛЬНОЙ ТРЕТИ - ПОРЯДОК, А НЕ НАБОР ПОРОГОВ.
 *
 * Пороги ставились по одному, по мере находок, и сложились в бессмыслицу:
 * название игры сжималось до одной буквы «Г», пока соседи держали полные
 * слова «Виджеты» и «Ссылка оверлея». Название отвечает на главный вопрос
 * полосы - «что сейчас в эфире», - и уходить оно обязано ПОСЛЕДНИМ.
 *
 * Пороги ЗАМЕРЕНЫ, а не подобраны: при полном составе треть занимает 757 px
 * (название 182, «Виджеты» 109, «Ссылка оверлея» 158, квота со шкалой и
 * меткой 268, поля и зазоры 40), и каждая ступень снимает ровно столько,
 * сколько весит её элемент. Замер сделан на самом длинном названии каталога
 * - «Стенка на стенку», - потому что порядок обязан держаться на худшем, а
 * не на среднем.
 *
 * ПЕРЕСЧИТАНО ЕДИНИЦЕЙ 21. Решение владельца 2026-09-29 (2) ужало заводскую
 * центральную треть с 650 до 540 px. По прежней лестнице на 1470 уходили
 * подпись «Оверлей», метка «Pro» и - целиком - кнопка «Виджеты»: единственный
 * вход в виджеты пропадал на заводской геометрии. Шкала расхода квоты -
 * украшение при числе, и она теперь уходит раньше подписи и значков.
 *
 * | Ширина трети | Что уходит                                    |
 * |--------------|-----------------------------------------------|
 * | < 800 px     | подпись «Виджеты» и шевроны - остаётся значок |
 * | < 640 px     | метка «Pro» у пилюли квоты                    |
 * | < 600 px     | шкала расхода квоты - остаётся число          |
 * | < 500 px     | подпись «Оверлей» - остаётся значок           |
 * | < 440 px     | значок «Виджеты»: кнопка уходит целиком       |
 * | < 400 px     | значок ссылки: кнопка уходит целиком          |
 * | < 380 px     | «из 60» у числа квоты                         |
 * | < 240 px     | название игры - целиком, а не до одной буквы  |
 *
 * СНАЧАЛА ОБЕ ПОДПИСИ, ПОТОМ МЕТКА «PRO». Первая редакция уводила метку
 * второй ступенью - и на окне по умолчанию (треть 647 px) её не оставалось
 * вовсе, хотя места хватало: подпись «Ссылка оверлея» весит 120 px, метка -
 * 40. Ворота полосы поймали это тем, что нажимать стало нечего.
 *
 * Между 380 и 240 название сокращается многоточием - и только там: к этому
 * моменту у соседей уже нечего снимать.
 *
 * ПРАВИЛО НАД ПОРОГАМИ: ни один элемент полосы не показывается короче трёх
 * символов с многоточием. Не помещается столько - элемент прячется целиком.
 * Огрызок «Г…» не называет игру и занимает место, на котором ничего не
 * написано. Правило проверяется воротами `console-rows` на всех положениях
 * границ, а не глазами на одном.
 *
 * Остаток эфира не прячется никогда - он в полосе именно ради этого.
 */

/** Общий каркас трети: контейнер запроса, без выхода за границу колонки. */
const THIRD = '@container flex h-full min-w-0 items-center overflow-hidden';

export function TopBarLeft() {
  return (
    <div data-control-row="topbar-left" className={`${THIRD} gap-1 px-3`}>
      <Brand href="/app" size="sm" />
      <div className="ms-auto flex shrink-0 items-center gap-1">
        <Notifications />
        <AccountMenu />
      </div>
    </div>
  );
}

export function TopBarCenter() {
  const session = useConsoleSession();

  return (
    <div data-control-row="topbar-center" className={`${THIRD} gap-2 px-3`}>
      {session.game === null ? (
        <span
          data-topbar="session-status"
          className="text-foreground/80 truncate text-sm @max-[340px]:hidden"
        >
          Эфир не запущен
        </span>
      ) : (
        /*
          Название игры - САМЫЙ ЗАМЕТНЫЙ элемент полосы, как у эталона.
          Остальное в полосе приглушено: игра отвечает на вопрос «что сейчас в
          эфире», и он важнее всех прочих.
        */
        /*
          КНОПКА ИГРЫ - РЯД, А НЕ СТОПКА.

          Прежде это была голая ссылка с одним `gap-2`: `Link`
          react-router рисует обычный `a`, ряда из него не получается, и
          на снимке владельца значок стоял над названием, а шеврон под ним
          - три строки вместо одной. Тот же корень, что у меню аккаунта.

          Вид даёт кнопка пресета, лестница деградации сохранена: `shrink`
          и пороги контейнера остаются классами раскладки.
        */
        <Button
          asChild
          variant="ghost"
          size="s"
          /* `shrink`: у кнопки пресета стоит `flex-shrink: 0`, и название
             игры не сжималось вовсе - при узком центре треть выносило на
             338 px при ширине 322. Многоточие уместнее наезда на соседа.

             `@max-[240px]:hidden` - нижняя ступень лестницы: уже этого
             название не сжимается, а уходит. */
          className="min-w-0 max-w-64 shrink @max-[240px]:hidden"
        >
          <Link to="/app/games" data-topbar="game-label">
            <Icon name="games" size="sm" />
            {/* `min-w-[2.75rem]` - те самые три символа с многоточием: ниже
                этого предела название не сокращается, а прячется целиком
                (правило полосы, см. заголовок файла). */}
            <span className="min-w-[2.75rem] truncate">{session.game}</span>
            {/* Стрелка - украшение: кнопка ведёт в каталог, а не
                раскрывает список. Уходит вместе с подписью «Виджеты»
                (800), освобождая место названию игры: на заводской трети
                именно этих пикселей не хватало, чтобы подпись ссылки
                оверлея поместилась, не сокращая название. */}
            <Icon name="chevronDown" size="sm" className="@max-[800px]:hidden" />
          </Link>
        </Button>
      )}

      {/*
        Порядок исчезновения при сужении центра: сперва «Виджеты», потом
        промо тарифа, потом подпись ссылки оверлея. Остаток эфира не прячется
        никогда - он в полосе именно потому, что обязан быть виден всегда.
      */}
      <div className="ms-auto flex shrink-0 items-center gap-1">
        {/* Ступени лестницы: подписи соседей уходят раньше значков, значки -
            раньше шкалы квоты, и всё это раньше названия игры. Пороги и
            порядок - в таблице в заголовке файла. */}
        <span className="@max-[440px]:hidden">
          <Widgets />
        </span>
        <span className="@max-[400px]:hidden">
          <OverlayUrl />
        </span>
        <span className="border-foreground/10 flex items-center gap-1 border-s ps-1">
          <QuotaPill />
          <QuotaPromo />
        </span>
      </div>
    </div>
  );
}

export function TopBarRight() {
  return (
    <div data-control-row="topbar-right" className={`${THIRD} gap-2 px-3`}>
      <SoundBar />
    </div>
  );
}
